import {
  Box,
  Button,
  ButtonBase,
  ButtonGroup,
  Card,
  CardContent,
  IconButton,
  MenuItem,
  Snackbar,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { financeApi } from "../lib/api";
import { useBlockedAccessGuard } from "../hooks/useBlockedAccessGuard";
import { transactionTypeLabels } from "../lib/labels";
import { CategoryIcon, categoryDisplayName, sortCategoriesHierarchically } from "../lib/format";
import { IconColorPicker } from "../components/IconColorPicker";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { SidePanel } from "../components/SidePanel";
import type { Category, CategoryPayload, Group, TransactionType } from "../lib/types";
import { queryKeys } from "../lib/queryKeys";

type CategoriesPageProps = {
  group: Group;
  onBlockedAccess?: () => void;
};

type CategoryMode = "active" | "deleted";

type ModalState =
  | { kind: "closed" }
  | { kind: "create"; initialType?: TransactionType; initialParentId?: string }
  | { kind: "edit"; category: Category };

type CategoryFormErrors = {
  name?: string;
};

type DeleteSnackbarState = {
  open: boolean;
};

const categoryTypeOrder: TransactionType[] = ["expense", "income", "transfer"];

function categoryLabel(category: Category, categories: Category[]) {
  return categoryDisplayName(category, categories);
}

function groupedCategories(categories: Category[]) {
  const grouped = new Map<TransactionType, Category[]>();
  for (const category of categories) {
    if (!grouped.has(category.type)) grouped.set(category.type, []);
    grouped.get(category.type)!.push(category);
  }
  return [
    ...categoryTypeOrder.filter((type) => grouped.has(type)),
    ...Array.from(grouped.keys()).filter((type) => !categoryTypeOrder.includes(type)).sort(),
  ].map((type) => [type, grouped.get(type)!.sort((left, right) => left.name.localeCompare(right.name))] as const);
}

function categoryTree(categories: Category[]) {
  const sortedCategories = sortCategoriesHierarchically(categories);
  const childrenByParent = new Map<string, Category[]>();
  for (const category of sortedCategories) {
    if (!category.parent_id) continue;
    if (!childrenByParent.has(category.parent_id)) childrenByParent.set(category.parent_id, []);
    childrenByParent.get(category.parent_id)!.push(category);
  }
  const topLevel = sortedCategories.filter((category) => !category.parent_id || !categories.some((item) => item.id === category.parent_id));
  return topLevel.map((category) => ({
    category,
    children: childrenByParent.get(category.id) || [],
  }));
}

function buildCategoryPayload(form: HTMLFormElement, icon: string): { payload?: CategoryPayload; error?: string } {
  const data = new FormData(form);
  const name = String(data.get("name") || "").trim();
  if (!name) return { error: "Name is required." };
  const type = String(data.get("type") || "expense") as TransactionType;
  const color = String(data.get("color") || "#2563eb");
  const parentId = String(data.get("parent_id") || "");
  const payload: CategoryPayload = { type, name, icon, color };
  if (parentId) payload.parent_id = parentId;
  return { payload };
}

function validateCategoryForm(form: HTMLFormElement): CategoryFormErrors {
  const data = new FormData(form);
  const errors: CategoryFormErrors = {};
  if (!String(data.get("name") || "").trim()) errors.name = "Name is required.";
  return errors;
}

function hasCategoryErrors(errors: CategoryFormErrors) {
  return Boolean(errors.name);
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => (typeof window === "undefined" ? false : window.matchMedia(query).matches));

  useEffect(() => {
    const media = window.matchMedia(query);
    setMatches(media.matches);
    const listener = (event: MediaQueryListEvent) => setMatches(event.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

function CategoryRow({ category, isChild, categories, onClick }: { category: Category; isChild?: boolean; categories: Category[]; onClick: () => void }) {
  const label = isChild ? `Subcategory · ${transactionTypeLabels[category.type] || category.type}` : transactionTypeLabels[category.type] || category.type;
  return (
    <ButtonBase className={`category-row category-${category.type}${isChild ? " child" : ""}${category.is_archived ? " archived" : ""}`} type="button" onClick={onClick}>
      <Box className="category-item-main">
        <CategoryIcon icon={category.icon} color={category.color} />
        <Box className="category-copy">
          <strong>{category.is_archived ? category.name : categoryLabel(category, categories)}</strong>
          <span>{category.is_archived ? `Deleted · ${label}` : label}</span>
        </Box>
      </Box>
    </ButtonBase>
  );
}

function CategoryModal({
  state,
  group,
  categories,
  onBack,
  onClose,
  onCloseAll,
  onDeleted,
  open,
}: {
  state: Exclude<ModalState, { kind: "closed" }>;
  group: Group;
  categories: Category[];
  onBack?: () => void;
  onClose: () => void;
  /** Close the whole panel stack (nested panel + parent) instead of one level. */
  onCloseAll?: () => void;
  onDeleted: () => void;
  open?: boolean;
}) {
  const queryClient = useQueryClient();
  const category = state.kind === "edit" ? state.category : null;
  const isSubcategory = Boolean(category?.parent_id);
  const isArchived = Boolean(category?.is_archived);
  const hasChildren = Boolean(category && categories.some((item) => item.parent_id === category.id && !item.is_archived));
  const [type, setType] = useState<TransactionType>(category?.type || (state.kind === "create" ? state.initialType : undefined) || "expense");
  const [icon, setIcon] = useState(category?.icon || "tag");
  const [color, setColor] = useState(category?.color || "#2563eb");
  const [parentId, setParentId] = useState(hasChildren ? "" : category?.parent_id || (state.kind === "create" ? state.initialParentId : undefined) || "");
  const [status, setStatus] = useState("");
  const [fieldErrors, setFieldErrors] = useState<CategoryFormErrors>({});
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [nestedModal, setNestedModal] = useState<ModalState>({ kind: "closed" });
  const [nestedPanelOpen, setNestedPanelOpen] = useState(false);
  const closeNestedTimer = useRef<number | undefined>(undefined);
  const animatedCloseRef = useRef(onClose);
  const requestAnimatedClose = () => animatedCloseRef.current();

  const parentOptions = useMemo(
    () =>
      sortCategoriesHierarchically(
        categories
          .filter((item) => !item.is_archived)
          .filter((item) => item.type === type)
          .filter((item) => !item.parent_id)
          .filter((item) => item.id !== category?.id),
        categories,
      ),
    [categories, category?.id, type],
  );

  const childCategories = useMemo(
    () =>
      category
        ? categories
            .filter((item) => item.parent_id === category.id)
            .filter((item) => item.is_archived === isArchived)
            .sort((left, right) => left.name.localeCompare(right.name))
        : [],
    [categories, category, isArchived],
  );

  const invalidateCategories = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["categories"] }),
      queryClient.invalidateQueries({ queryKey: queryKeys.records }),
      queryClient.invalidateQueries({ queryKey: queryKeys.deletedRecords }),
      queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
    ]);
  };

  const saveMutation = useMutation({
    mutationFn: (payload: CategoryPayload) =>
      category ? financeApi.updateCategory(group.id, category.id, payload) : financeApi.createCategory(group.id, payload),
    onSuccess: async () => {
      await invalidateCategories();
      requestAnimatedClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: () => financeApi.deleteCategory(group.id, category!.id),
    onSuccess: async () => {
      await invalidateCategories();
      onDeleted();
      requestAnimatedClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const restoreMutation = useMutation({
    mutationFn: () => financeApi.restoreCategory(group.id, category!.id),
    onSuccess: async () => {
      await invalidateCategories();
      onClose();
    },
    onError: (error) => setStatus(error instanceof Error ? error.message : "Request failed"),
  });

  const selectedParent = parentId ? categories.find((item) => item.id === parentId) : null;

  useEffect(() => () => {
    if (closeNestedTimer.current) window.clearTimeout(closeNestedTimer.current);
  }, []);

  const openNestedCategory = (nextModal: Exclude<ModalState, { kind: "closed" }>) => {
    if (closeNestedTimer.current) window.clearTimeout(closeNestedTimer.current);
    setNestedPanelOpen(false);
    setNestedModal(nextModal);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setNestedPanelOpen(true));
    });
  };

  const closeNestedCategory = () => {
    setNestedPanelOpen(false);
    if (closeNestedTimer.current) window.clearTimeout(closeNestedTimer.current);
    closeNestedTimer.current = window.setTimeout(() => {
      setNestedModal({ kind: "closed" });
    }, 260);
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isArchived) return;
    setStatus("");
    const errors = validateCategoryForm(event.currentTarget);
    setFieldErrors(errors);
    if (hasCategoryErrors(errors)) return;
    const { payload, error } = buildCategoryPayload(event.currentTarget, icon);
    if (error) {
      setStatus(error);
      return;
    }
    saveMutation.mutate(payload!);
  };

  return (
    <>
      <SidePanel
        ariaLabelledBy="categoryModalTitle"
        className="category-modal"
        dataTestId={category ? "category-edit-modal" : "category-create-modal"}
        open={open}
        onClose={onClose}
      >
        {({ close }) => {
          animatedCloseRef.current = close;
          return (
            <>
          <Box className="records-modal-toolbar">
            {onBack ? (
              <IconButton className="records-icon-button" type="button" aria-label="Back to parent category" title="Back" onClick={onBack}>
                <ArrowBackOutlinedIcon fontSize="small" />
              </IconButton>
            ) : null}
            <h2 id="categoryModalTitle">
              {isArchived ? (isSubcategory ? "Deleted subcategory" : "Deleted category") : category ? (isSubcategory ? "Edit subcategory" : "Edit category") : "Add category"}
              {category?.import_id ? (
                <Tooltip title="This category was created from an import.">
                  <FileSpreadsheet className="record-import-icon" size={18} aria-label="Imported category" />
                </Tooltip>
              ) : null}
            </h2>
            <IconButton
              className="records-icon-button"
              type="button"
              aria-label="Close category dialog"
              title="Close"
              onClick={() => (onCloseAll ? onCloseAll() : close())}
            >
              <svg viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M18 6 6 18"></path>
                <path d="m6 6 12 12"></path>
              </svg>
            </IconButton>
          </Box>
          <form className="records-form category-form" noValidate onSubmit={submit}>
            <Box className="category-type-toggle">
              <input name="type" type="hidden" value={type} />
              <ButtonGroup variant="outlined" aria-label="Category type">
                <Button
                  type="button"
                  variant={type === "expense" ? "contained" : "outlined"}
                  disabled={isArchived}
                  onClick={() => {
                    setType("expense");
                    setParentId("");
                  }}
                >
                  Expense
                </Button>
                <Button
                  type="button"
                  variant={type === "income" ? "contained" : "outlined"}
                  disabled={isArchived}
                  onClick={() => {
                    setType("income");
                    setParentId("");
                  }}
                >
                  Income
                </Button>
              </ButtonGroup>
            </Box>
            <Box className="records-field">
              <TextField
                select
                label="Parent category"
                name="parent_id"
                value={parentId}
                disabled={isArchived || hasChildren}
                slotProps={{
                  select: {
                    renderValue: () =>
                      selectedParent ? (
                        <span className="records-select-option">
                          <CategoryIcon icon={selectedParent.icon} color={selectedParent.color} />
                          <span>{selectedParent.name}</span>
                        </span>
                      ) : (
                        "No parent"
                      ),
                  },
                }}
                onChange={(event) => setParentId(String(event.target.value))}
              >
                <MenuItem value="">No parent</MenuItem>
                {parentOptions.map((option) => (
                  <MenuItem key={option.id} value={option.id}>
                    <span className="records-select-option">
                      <CategoryIcon icon={option.icon} color={option.color} />
                      <span>{option.name}</span>
                    </span>
                  </MenuItem>
                ))}
              </TextField>
            </Box>
            <Box className="category-controls-row">
              <IconColorPicker
                color={color}
                colorAriaLabel="Choose category color"
                colorTitle="Category color"
                disabled={isArchived}
                icon={icon}
                iconAriaLabel="Choose category icon"
                iconInputId="categoryIcon"
                pickerId="categoryIconPicker"
                pickerLabel="Category icon"
                onColorChange={setColor}
                onIconChange={setIcon}
              />
              <Box className="records-field">
                <TextField
                  required
                  error={Boolean(fieldErrors.name)}
                  helperText={fieldErrors.name}
                  id="outlined-required"
                  label="Name"
                  name="name"
                  placeholder="My category"
                  defaultValue={category?.name || ""}
                  disabled={isArchived}
                  fullWidth
                  onChange={() => setFieldErrors((current) => ({ ...current, name: undefined }))}
                />
              </Box>
            </Box>
            {childCategories.length ? (
              <Box className="category-panel-subcategories">
                <h3>Subcategories</h3>
                <Box className="category-panel-subcategory-list">
                  {childCategories.map((child) => (
                    <CategoryRow key={child.id} category={child} isChild categories={categories} onClick={() => openNestedCategory({ kind: "edit", category: child })} />
                  ))}
                </Box>
              </Box>
            ) : null}
            <Box className="records-modal-actions">
              {confirmDelete ? (
                <>
                  <Button variant="outlined" type="button" disabled={deleteMutation.isPending} onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                  <Button data-testid="confirm-delete-category-button" variant="outlined" color="error" type="button" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate()}>
                    Confirm delete
                  </Button>
                </>
              ) : (
                <>
                  {isArchived ? (
                    <Button variant="outlined" type="button" disabled={restoreMutation.isPending} onClick={() => restoreMutation.mutate()}>
                      Restore category
                    </Button>
                  ) : null}
                  {category && !isArchived ? (
                    <Button data-testid="delete-category-button" variant="outlined" color="error" type="button" disabled={deleteMutation.isPending} onClick={() => setConfirmDelete(true)}>
                      Delete category
                    </Button>
                  ) : null}
                  {category && !isArchived && !category.parent_id ? (
                    <Button data-testid="add-subcategory-button" variant="outlined" type="button" onClick={() => openNestedCategory({ kind: "create", initialType: category.type, initialParentId: category.id })}>
                      Add subcategory
                    </Button>
                  ) : null}
                  {!isArchived ? (
                    <Button variant="contained" type="submit" disabled={saveMutation.isPending}>
                      {category ? "Save changes" : "Create category"}
                    </Button>
                  ) : null}
                </>
              )}
            </Box>
          </form>
          {status ? <p className="status error">{status}</p> : null}
            </>
          );
        }}
      </SidePanel>

      {nestedModal.kind !== "closed" ? (
        <CategoryModal
          key={nestedModal.kind === "edit" ? `nested-edit-${nestedModal.category.id}` : `nested-create-${nestedModal.initialType || "expense"}-${nestedModal.initialParentId || "root"}`}
          state={nestedModal}
          group={group}
          categories={categories}
          open={nestedPanelOpen}
          onBack={closeNestedCategory}
          onClose={closeNestedCategory}
          onCloseAll={() => {
            closeNestedCategory();
            if (onCloseAll) onCloseAll();
            else requestAnimatedClose();
          }}
          onDeleted={onDeleted}
        />
      ) : null}
    </>
  );
}

export function CategoriesPage({ group, onBlockedAccess }: CategoriesPageProps) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<CategoryMode>("active");
  const [modal, setModal] = useState<ModalState>({ kind: "closed" });
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [emptyError, setEmptyError] = useState("");
  const [deleteSnackbar, setDeleteSnackbar] = useState<DeleteSnackbarState>({ open: false });
  const isMobile = useMediaQuery("(max-width: 980px)");

  const categoriesQuery = useQuery({ queryKey: queryKeys.categories(group.id), queryFn: () => financeApi.categories(group.id) });
  const categories = categoriesQuery.data || [];
  const activeCategories = categories.filter((category) => !category.is_archived);
  const deletedCategories = categories.filter((category) => category.is_archived);

  useBlockedAccessGuard([categoriesQuery.error], onBlockedAccess);
  const visibleCategories = mode === "deleted" ? deletedCategories : activeCategories;

  const emptyDeletedMutation = useMutation({
    mutationFn: () => financeApi.emptyDeletedCategories(group.id),
    onSuccess: async () => {
      setConfirmEmpty(false);
      setEmptyError("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["categories"] }),
        queryClient.invalidateQueries({ queryKey: queryKeys.records }),
        queryClient.invalidateQueries({ queryKey: queryKeys.summary }),
      ]);
    },
    onError: (error) => setEmptyError(error instanceof Error ? error.message : "Request failed"),
  });

  return (
    <>
      <header className="topbar">
        <Box>
          <h1>{mode === "deleted" ? "Deleted categories" : "Categories"}</h1>
          <p>Shared category names, icons, colors, and subcategories.</p>
        </Box>
        {mode === "active" ? (
          <Box className="records-topbar-actions">
            <Button className="records-desktop-add-action" variant="contained" type="button" startIcon={<AddIcon fontSize="small" />} onClick={() => setModal({ kind: "create" })}>
              Add category
            </Button>
            <IconButton className="records-mobile-add-action" type="button" aria-label="Add category" title="Add category" onClick={() => setModal({ kind: "create" })}>
              <AddIcon />
            </IconButton>
          </Box>
        ) : null}
      </header>

      <section className="content">
        <Box className="category-page-stack">
          {mode === "active" ? (
            deletedCategories.length ? (
              <Card className="records-panel categories-actions-panel">
                <CardContent className="records-panel-content">
                  <Box className="records-toolbar">
                    <Box className="records-utility-actions">
                      <Button variant="outlined" type="button" startIcon={<DeleteOutlineIcon fontSize="small" />} onClick={() => setMode("deleted")}>
                        Deleted categories
                      </Button>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ) : null
          ) : (
            <Card className="records-panel categories-actions-panel">
              <CardContent className="records-panel-content">
                <Box className="records-toolbar">
                  <>
                    <Box className="records-primary-actions"></Box>
                    <Box className="records-utility-actions">
                      {deletedCategories.length ? (
                        <Button variant="outlined" color="error" type="button" onClick={() => setConfirmEmpty(true)}>
                          Empty trash
                        </Button>
                      ) : null}
                      <Button variant="outlined" type="button" onClick={() => setMode("active")}>
                        Back to categories
                      </Button>
                    </Box>
                  </>
                </Box>
              </CardContent>
            </Card>
          )}

          {categoriesQuery.isLoading ? <p className="status">Loading categories...</p> : null}
          {!categoriesQuery.isLoading && !visibleCategories.length ? <p className="status">{mode === "deleted" ? "No deleted categories." : "No categories yet."}</p> : null}

          {mode === "active" ? (
            <Box className="category-list">
              {groupedCategories(activeCategories).map(([type, typeCategories]) => (
                <Card className={`records-panel categories-panel category-group-panel category-group-${type}`} key={type}>
                  <CardContent className="records-panel-content">
                    <Box className="category-group">
                    <h3>{transactionTypeLabels[type] || type}</h3>
                    <Box className="category-group-list">
                      {categoryTree(typeCategories).map(({ category, children }) => (
                        <Box className={`category-tree-branch${children.length ? " with-children" : ""}`} key={category.id}>
                          <CategoryRow category={category} categories={categories} onClick={() => setModal({ kind: "edit", category })} />
                          {children.length ? (
                            <Box className="category-children">
                              {children.map((child) => (
                                <CategoryRow key={child.id} category={child} isChild categories={categories} onClick={() => setModal({ kind: "edit", category: child })} />
                              ))}
                            </Box>
                          ) : null}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Card className="records-panel categories-panel category-trash-panel">
              <CardContent className="records-panel-content">
                <Box className="category-group category-trash-group">
                <Box className="category-group-list">
                  {deletedCategories.map((category) => (
                    <CategoryRow key={category.id} category={category} categories={categories} onClick={() => setModal({ kind: "edit", category })} />
                  ))}
                </Box>
              </Box>
              </CardContent>
            </Card>
          )}
        </Box>
      </section>

      {modal.kind !== "closed" ? (
        <CategoryModal
          key={modal.kind === "edit" ? `edit-${modal.category.id}` : `create-${modal.initialType || "expense"}-${modal.initialParentId || "root"}`}
          state={modal}
          group={group}
          categories={categories}
          onClose={() => setModal({ kind: "closed" })}
          onDeleted={() => setDeleteSnackbar({ open: true })}
        />
      ) : null}

      {confirmEmpty ? (
        <ConfirmDialog
          titleId="emptyCategoryTrashTitle"
          title="Empty deleted categories?"
          description="This permanently deletes all categories from Deleted and cannot be undone. Records that used those categories will keep the record data without a category."
          confirmLabel="Empty trash"
          closeAriaLabel="Close empty deleted categories dialog"
          pending={emptyDeletedMutation.isPending}
          error={emptyError}
          onConfirm={() => emptyDeletedMutation.mutate()}
          onClose={() => setConfirmEmpty(false)}
        />
      ) : null}

      <Snackbar
        className="category-delete-snackbar"
        data-testid="category-delete-snackbar"
        open={deleteSnackbar.open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: "top", horizontal: isMobile ? "center" : "left" }}
        onClose={(_, reason) => {
          if (reason === "clickaway") return;
          setDeleteSnackbar({ open: false });
        }}
        message={
          <span>
            Category deleted. You can find it in{" "}
            <button
              className="snackbar-link"
              data-testid="deleted-categories-snackbar-link"
              type="button"
              onClick={() => {
                setMode("deleted");
                setDeleteSnackbar({ open: false });
              }}
            >
              Deleted categories
            </button>
          </span>
        }
      />
    </>
  );
}
